import React, {
	useState,
	useEffect,
	forwardRef,
	type ReactElement,
	useRef,
	useCallback,
} from 'react';
import { styled } from '@compiled/react';
import {
	NudgeSpotlight,
	type NudgeType,
	type NudgeSpotlightRef,
} from '@atlassiansox/nudge-tooltip';
import { SpotlightPulse } from '@atlaskit/onboarding';
import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import CoordinationClient from '@atlassian/jira-engagement';
import { getIdPHost } from '@atlassian/jira-global-permissions/src/common/utils';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import { useAccountId, useEnvironment } from '@atlassian/jira-tenant-context-controller';
import { APP_SWITCHER_NUDGES } from './constants';
import messages from './messages';
import type { CoordinationStopProviderProps, StopFunction, AppSwitcherNudgeType } from './types';
import { getUserHaveSeenSpotlightProperty, setUserHaveSeenSpotlightProperty } from './utils';

const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

const StyledNudge: NudgeType = forwardRef(({ children, nudgeTestId, ...restProps }, ref) => (
	<NudgeWrapper {...restProps} ref={ref} data-testid={nudgeTestId}>
		{children}
	</NudgeWrapper>
));

const AppSwitcherNudge = ({
	nudge,
	fallback,
	children,
	cloudId,
	setUserProperty,
}: {
	nudge: AppSwitcherNudgeType;
	fallback: ReactElement;
	children: ReactElement;
	cloudId: string;
	setUserProperty: () => void;
}) => {
	const { formatMessage } = useIntl();
	const { data: orgId } = useOrgId();
	const [dismissed, setDismissed] = useState(false);
	const { messageId } = nudge;
	const environment = useEnvironment();
	const host = getIdPHost(environment);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hideMessage = useCallback(
		(stop?: StopFunction) => {
			setDismissed(true);
			stop && stop();
			fireTrackAnalytics(createAnalyticsEvent({}), 'endUserActivateSpotlight dismissed', {
				messageId,
			});
		},
		[createAnalyticsEvent, messageId],
	);

	const handleFireDismissButtonClickEvent = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({});
		fireUIAnalytics(analyticsEvent, 'button clicked', 'endUserActivateSpotlightDismissButton', {
			messageId,
		});
	}, [createAnalyticsEvent, messageId]);

	const handleFireTourButtonClickEvent = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({});
		fireUIAnalytics(analyticsEvent, 'button clicked', 'endUserActivateSpotlightTourButton', {
			messageId,
		});
	}, [createAnalyticsEvent, messageId]);

	const nudgeSpotlightRef = useRef<NudgeSpotlightRef>({});
	return (
		<CoordinationClient messageId={messageId} fallback={fallback}>
			<CoordinationStopProvider>
				{(stop) => (
					<ContextualAnalyticsData sourceName="endUserActivateSpotlightNudge" sourceType={SCREEN}>
						<FireScreenAnalytics attributes={{ messageId }} />
						<NudgeSpotlight
							width={276}
							hidden={dismissed}
							setHidden={() => hideMessage(stop)}
							heading={formatMessage(nudge.heading)}
							content={
								<ContextualAnalyticsData sourceName="endUserActivateSpotlight" sourceType={SCREEN}>
									{formatMessage(nudge.content)}
									<FireScreenAnalytics attributes={{ messageId }} />
								</ContextualAnalyticsData>
							}
							image={
								<Inline xcss={imgStyles}>
									<img alt="" src={nudge.image} />
								</Inline>
							}
							actions={[
								{
									text: formatMessage(nudge.primaryActionText),
									onClick: () => {
										handleFireTourButtonClickEvent();
										nudge.primaryOnClickAction(host, orgId, cloudId);
									},
								},
								{
									text: formatMessage(messages.secondaryAction),
									onClick: () => {
										handleFireDismissButtonClickEvent();
										hideMessage(stop);
									},
								},
							]}
							position="bottom-end"
							onOutsideClick={() => hideMessage(stop)}
							nudgeTestId={`${messageId}-nudge`}
							cardTestId={`${messageId}-card`}
							showSpotlightCardOnFirstRender={false}
							onShow={() => setUserProperty()}
							nudge={StyledNudge}
							ref={nudgeSpotlightRef}
						>
							<SpotlightPulse
								testId={`${messageId}-nudge-pulsing-border`}
								radius={50}
								pulse={!dismissed}
								onFocus={() => {
									!dismissed && nudgeSpotlightRef.current?.toggleCardVisibility?.(true);
								}}
							>
								{children}
							</SpotlightPulse>
						</NudgeSpotlight>
					</ContextualAnalyticsData>
				)}
			</CoordinationStopProvider>
		</CoordinationClient>
	);
};

export const AppSwitcherNudgeToolTip = ({
	children,
	cloudId,
}: {
	children: ReactElement;
	cloudId: string;
}) => {
	const accountId = useAccountId();
	const [filteredNudges, setFilteredNudges] = useState<AppSwitcherNudgeType[]>([]);

	useEffect(() => {
		Promise.all(
			APP_SWITCHER_NUDGES.map((nudge) =>
				getUserHaveSeenSpotlightProperty(accountId, nudge.messageId),
			),
		).then((properties) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const seenNudgeIds = properties.filter((nudgeId) => nudgeId !== null) as string[];
			setFilteredNudges(
				APP_SWITCHER_NUDGES.filter((nudge) => !seenNudgeIds.includes(nudge.messageId)),
			);
		});
	}, [accountId]);

	return filteredNudges.reduce((acc, nudge) => {
		const setUserProperty = () => setUserHaveSeenSpotlightProperty(accountId, nudge.messageId);
		return (
			<AppSwitcherNudge
				nudge={nudge}
				fallback={acc}
				cloudId={cloudId}
				setUserProperty={setUserProperty}
			>
				{children}
			</AppSwitcherNudge>
		);
	}, children);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NudgeWrapper = styled.div({
	// previous app switcher button inside have 2px margin left and right
	margin: `0 ${token('space.025', '2px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		margin: 0,
		'&:hover, &:focus, &:active, &:focus-visible': { margin: 0 },
	},
});

const imgStyles = xcss({
	height: '154px',
	width: '276px',
	marginBottom: 'space.negative.050',
});
